import React, {useEffect, useRef, useState} from "react";
import {useTheme} from "@mui/material/styles";
import {LibrettoProjectTypes} from "../../utils/utils";
import {isSupportedChromeEdgeOrBrave, unsupportedEditorBrowserMessage} from "../../utils/browserSupport";
import {useAssetStore} from "../../storage/AssetStore";
import {
    SingleFileReviewState,
    SingleFileSelectState,
    WorkflowUploadHeader,
    WorkflowUploadShell,
    getWorkflowUploadFiletype,
} from "./WorkflowUploadCardShared";
import {
    validateSingleWorkflowFileSelection,
    validateWorkflowRequiresAudioStream,
} from "./workflowUploadValidation";

const viewStates = {
    SELECT_FILE: "select-file",
    REVIEW_AND_UPLOAD: "review-and-upload",
};

const UploadAndEditWorkflow = ({
    onClose,
    projectId = null,
    initialFile = null,
    onUploadInitiated,
    title = "Upload and start editing",
    subtitle = "Opens the editor with your file so you can edit by text or on the timeline.",
    selectTitle = "Upload video, audio, or image",
    selectHelperText = "Click to browse or drag and drop your file.",
    accept = "video/*,audio/*,image/*",
    allowImages = true,
    requireAudioStream = false,
    missingAudioValidationMessage = "This file has no audio stream. This workflow requires audio.",
    projectType = LibrettoProjectTypes.ProjectTypeEditor,
    queueEnhancementOnUpload = false,
    notifyParentOnServerInitiated = true,
    submitLabel = "Upload",
    languageDescription = "Set language for speech transcription quality.",
    imageNotAllowedMessage = "Images are not supported in this workflow. Upload an audio or video file.",
    inputId = "upload-and-edit-upload",
    onUseSample,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const {UploadAsset} = useAssetStore();

    const [selectedFile, setSelectedFile] = useState(initialFile);
    const [selectedLanguage, setSelectedLanguage] = useState("auto");
    const [validationMessage, setValidationMessage] = useState("");
    const [isStartingUpload, setIsStartingUpload] = useState(false);
    const [currentView, setCurrentView] = useState(
        initialFile ? viewStates.REVIEW_AND_UPLOAD : viewStates.SELECT_FILE
    );
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!initialFile) return;
        setSelectedFile(initialFile);
        setCurrentView(viewStates.REVIEW_AND_UPLOAD);
        setValidationMessage("");
    }, [initialFile]);

    const handleFileChoose = async (file) => {
        const nextValidationMessage = await validateSingleWorkflowFileSelection(file, {
            allowImages,
            imageNotAllowedMessage,
        });
        if (nextValidationMessage) {
            setValidationMessage(nextValidationMessage);
            return;
        }

        setSelectedFile(file);
        setCurrentView(viewStates.REVIEW_AND_UPLOAD);
        setValidationMessage("");
    };

    const handleUpload = async () => {
        if (!selectedFile || isStartingUpload) return;
        if (!isSupportedChromeEdgeOrBrave()) {
            setValidationMessage(unsupportedEditorBrowserMessage);
            return;
        }

        setIsStartingUpload(true);
        setValidationMessage("");

        try {
            if (requireAudioStream) {
                const audioValidationMessage = await validateWorkflowRequiresAudioStream(selectedFile, {
                    missingAudioMessage: missingAudioValidationMessage,
                });
                if (audioValidationMessage) {
                    setValidationMessage(audioValidationMessage);
                    return;
                }
            }

            const filetype = getWorkflowUploadFiletype(selectedFile);
            let uploadProjectIdResolved = String(projectId || "").trim();
            let uploadAssetIdResolved = "";
            let didNotifyParent = false;

            const maybeNotifyParent = () => {
                if (didNotifyParent) return;
                const resolvedAssetId = String(uploadAssetIdResolved || "").trim();
                const resolvedProjectId = String(uploadProjectIdResolved || projectId || "").trim();
                if (!resolvedAssetId || !resolvedProjectId || !onUploadInitiated) return;

                didNotifyParent = true;
                onUploadInitiated({
                    assetId: resolvedAssetId,
                    projectId: resolvedProjectId,
                    languageCode: selectedLanguage,
                });
            };

            const assetId = await UploadAsset({
                projectId: uploadProjectIdResolved || projectId,
                projectType,
                file: selectedFile,
                filetype,
                languageCode: selectedLanguage,
                queueEnhancementOnUpload,
                onUploadInitiated: ({assetId: serverAssetId, projectId: serverProjectId}) => {
                    const resolvedAssetId = String(serverAssetId || "").trim();
                    if (resolvedAssetId) uploadAssetIdResolved = resolvedAssetId;
                    const resolvedProjectId = String(serverProjectId || "").trim();
                    if (resolvedProjectId) uploadProjectIdResolved = resolvedProjectId;
                    if (notifyParentOnServerInitiated) {
                        maybeNotifyParent();
                    }
                },
            });

            if (!assetId) {
                throw new Error("Upload failed to return an asset id");
            }

            uploadAssetIdResolved = String(assetId || "").trim();
            maybeNotifyParent();

            if (!onUploadInitiated) {
                onClose?.();
            }
        } catch (error) {
            console.error("[UploadAndEditWorkflow] Upload failed", error);
            if (isMountedRef.current) {
                setValidationMessage("Could not start upload. Please try again.");
            }
        } finally {
            if (isMountedRef.current) {
                setIsStartingUpload(false);
            }
        }
    };

    const handleSwapFile = () => {
        setCurrentView(viewStates.SELECT_FILE);
        setValidationMessage("");
    };

    return (
        <WorkflowUploadShell>
            <WorkflowUploadHeader onClose={onClose} title={title} subtitle={subtitle}/>

            {currentView === viewStates.SELECT_FILE ? (
                <SingleFileSelectState
                    onFileChoose={handleFileChoose}
                    validationMessage={validationMessage}
                    isDarkMode={isDarkMode}
                    accept={accept}
                    uploadTitle={selectTitle}
                    uploadHelperText={selectHelperText}
                    inputId={inputId}
                    onUseSample={onUseSample}
                />
            ) : null}

            {currentView === viewStates.REVIEW_AND_UPLOAD ? (
                <SingleFileReviewState
                    selectedFile={selectedFile}
                    selectedLanguage={selectedLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                    onSubmit={handleUpload}
                    onSwapFile={handleSwapFile}
                    validationMessage={validationMessage}
                    isSubmitting={isStartingUpload}
                    submitLabel={submitLabel}
                    languageDescription={languageDescription}
                />
            ) : null}
        </WorkflowUploadShell>
    );
};

export default UploadAndEditWorkflow;
